'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { CheckGlyph, RetryGlyph } from '../../../internal/icons.js';
import { Spinner } from '../../feedback/spinner/index.js';
import { Switch } from '../switch/index.js';
const DEFAULT_LABELS = {
    saving: 'Saving',
    saved: 'Saved',
    error: 'Couldn’t save.',
    retry: 'Retry',
};
function defaultMessage(error) {
    return error instanceof Error && error.message ? error.message : null;
}
const ListContext = createContext({
    labels: DEFAULT_LABELS,
    savedDuration: 2000,
    getErrorMessage: defaultMessage,
});
/**
 * A group of on/off settings that each save on their own, such as
 * notification preferences. Rows toggle optimistically, show their own
 * saving and error state with retry, and roll back if the save fails.
 */
export const SwitchSettingsList = forwardRef(function SwitchSettingsList({ title, description, headingLevel: H = 'h3', labels, savedDuration = 2000, getErrorMessage = defaultMessage, className, children, ...rest }, ref) {
    const auto = useId();
    const titleId = `mn-switch-settings-${auto}-title`;
    const descriptionId = `mn-switch-settings-${auto}-description`;
    const value = useMemo(() => ({ labels: { ...DEFAULT_LABELS, ...labels }, savedDuration, getErrorMessage }), [labels, savedDuration, getErrorMessage]);
    const named = Boolean(title) || Boolean(rest['aria-label']) || Boolean(rest['aria-labelledby']);
    return (_jsx(ListContext.Provider, { value: value, children: _jsxs("div", { ref: ref, role: named ? 'group' : undefined, "aria-labelledby": title ? titleId : undefined, "aria-describedby": description ? descriptionId : undefined, ...rest, className: cx('mn-switch-settings-list', className), children: [title || description ? (_jsxs("div", { className: "mn-switch-settings-list__header", children: [title ? (_jsx(H, { id: titleId, className: "mn-switch-settings-list__title", children: title })) : null, description ? (_jsx("p", { id: descriptionId, className: "mn-switch-settings-list__description", children: description })) : null] })) : null, _jsx("ul", { className: "mn-switch-settings-list__rows", children: children })] }) }));
});
/** One setting row: label and description on the left, the switch and its save status on the right. */
export const SwitchSettingsItem = forwardRef(function SwitchSettingsItem({ label, description, checked: checkedProp, defaultChecked = false, onCheckedChange, disabled = false, labels: rowLabels, onStatusChange, className, ...rest }, ref) {
    const list = useContext(ListContext);
    const labels = { ...list.labels, ...rowLabels };
    const auto = useId();
    const base = `mn-switch-settings-${auto}`;
    const switchId = `${base}-switch`;
    const labelId = `${base}-label`;
    const descriptionId = `${base}-description`;
    const statusId = `${base}-status`;
    const retryId = `${base}-retry`;
    const [inner, setInner] = useState(defaultChecked);
    const saved = checkedProp ?? inner;
    // The optimistic value while saving (and, controlled, until the parent's
    // `checked` catches up with a successful save).
    const [pending, setPending] = useState(null);
    const [status, setStatusState] = useState('idle');
    const [message, setMessage] = useState(null);
    const [retryValue, setRetryValue] = useState(null);
    const timer = useRef(undefined);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(timer.current);
        };
    }, []);
    const statusRef = useRef(status);
    const setStatus = useCallback((next) => {
        statusRef.current = next;
        setStatusState(next);
        onStatusChange?.(next);
    }, [onStatusChange]);
    // A new `checked` from the parent is the truth, unless a save is in flight.
    useEffect(() => {
        if (statusRef.current !== 'saving')
            setPending(null);
    }, [checkedProp]);
    const shown = pending ?? saved;
    const save = async (next) => {
        if (statusRef.current === 'saving' || disabled)
            return;
        clearTimeout(timer.current);
        setRetryValue(null);
        setPending(next);
        setMessage(null);
        setStatus('saving');
        try {
            const result = onCheckedChange?.(next);
            const async = Boolean(result && typeof result.then === 'function');
            await result;
            if (!mounted.current)
                return;
            if (checkedProp === undefined) {
                setInner(next);
                setPending(null);
            }
            else if (checkedProp === next) {
                setPending(null);
            }
            if (async) {
                setStatus('saved');
                timer.current = setTimeout(() => {
                    if (mounted.current)
                        setStatus('idle');
                }, list.savedDuration);
            }
            else {
                setStatus('idle');
            }
        }
        catch (error) {
            if (!mounted.current)
                return;
            setPending(null);
            setRetryValue(next);
            setMessage(list.getErrorMessage(error));
            setStatus('error');
        }
    };
    const announcement = status === 'saved'
        ? labels.saved
        : status === 'error'
            ? [labels.error, typeof message === 'string' ? message : null].filter(Boolean).join(' ')
            : '';
    return (_jsxs("li", { ref: ref, ...rest, className: cx('mn-switch-settings-list__row', className), "data-status": status, "data-disabled": disabled || undefined, children: [_jsxs("div", { className: "mn-switch-settings-list__text", children: [_jsx("label", { id: labelId, htmlFor: switchId, className: "mn-switch-settings-list__label", children: label }), description ? (_jsx("p", { id: descriptionId, className: "mn-switch-settings-list__item-description", children: description })) : null, status === 'error' ? (_jsxs("p", { className: "mn-switch-settings-list__error", "aria-hidden": true, children: [labels.error, message ? _jsxs(_Fragment, { children: [" ", message] }) : null] })) : null] }), _jsxs("div", { className: "mn-switch-settings-list__control", children: [status === 'saving' ? (_jsx(Spinner, { size: "sm", label: labels.saving, className: "mn-switch-settings-list__spinner" })) : status === 'saved' ? (_jsxs("span", { className: "mn-switch-settings-list__saved", "aria-hidden": true, children: [_jsx(CheckGlyph, {}), labels.saved] })) : status === 'error' && retryValue !== null ? (_jsxs("button", { type: "button", id: retryId, "aria-labelledby": `${retryId} ${labelId}`, className: "mn-switch-settings-list__retry", onClick: () => void save(retryValue), children: [_jsx(RetryGlyph, { "aria-hidden": true }), labels.retry] })) : null, _jsx(Switch, { id: switchId, checked: shown, disabled: disabled, "aria-describedby": cx(description ? descriptionId : undefined, status === 'error' ? statusId : undefined), "aria-busy": status === 'saving' || undefined, onCheckedChange: (next) => void save(next) })] }), _jsx("span", { id: statusId, className: "mn-visually-hidden", role: "status", children: announcement })] }));
});
//# sourceMappingURL=switch-settings-list.js.map